"use client";

import Image from "next/image";
import Heading from "~/componentsDS/Typography/Heading";
import Paragraph from "~/componentsDS/Typography/Paragraph";
import Reveal from "~/componentsDS/Reveal";
import ElementorContainer from "~/componentsDS/ElementorLoyout/ElementorContainer";
import { cn } from "~/lib/utils";
import { pricingItems } from "../data/pricing-data";
import { TOTAL_VALUE_OESTRA_TABS } from "~/app/(main)/pqp/anti-aging-face-cream/config/totalValueTabs";
import TotalValueBadge from "~/componentsDS/TotalValueBadge";
import useTotalValue from "~/hooks/useTotalValue";
import DoseAdjustmentNote from "~/componentsDS/DoseAdjustmentNote";
import type { InfoBadgeEventData } from "~/componentsDS/InfoBadge";

type Props = {
    className?: string;
    imageAlt?: string;
    totalValueEventData?: InfoBadgeEventData | null;
};

const defaultTotalValueEventData: InfoBadgeEventData = {
    event_name: "lp_interaction",
    widget: "transparent_pricing",
};

type OngoingPriceHeadingProps = {
    size?: "2xs" | "xs";
    sizes?: { md?: "2xs" | "xs" };
};

const OngoingPriceHeading = ({ size = "xs", sizes }: OngoingPriceHeadingProps) => (
    <Heading as="h3" size={size} sizes={sizes} weight="400" className="mb-4 md:mb-6">
        Then $99.50 per month
    </Heading>
);

const LegacyPricingHeadings = () => (
    <>
        <Heading as="h3" size="xs" weight="400" className="[&>i]:font-featureDisplay [&>i]:font-normal [&>i]:inline [&>i]:text-[21px] max-md:hidden">
            $199 per month for your <i>first 6 months</i>
        </Heading>

        <Heading as="h3" size="xs" weight="400" className="[&>i]:font-featureDisplay [&>i]:font-normal [&>i]:inline [&>i]:text-[21px] md:hidden">
            $199/month for your <i>first 6 months</i>
        </Heading>

        <OngoingPriceHeading />
    </>
);

const RedesignPricingHeadings = ({ eventData }: {
    eventData?: InfoBadgeEventData;
}) => (
    <>
        <div className="flex max-md:flex-wrap items-center max-md:justify-center mb-2">
            <Heading
                as="h3"
                size="2xs"
                sizes={{ md: "xs" }}
                weight="400"
                className="hidden md:inline-block leading-none pt-1"
            >
                $199
            </Heading>
            <Heading as="h3" size="2xs" sizes={{ md: "xs" }} weight="400" className="md:hidden">$199/month</Heading>
            <TotalValueBadge
                className="mx-2.5 max-md:mb-1 self-center"
                variant="primary"
                size="lg"
                label="Total Value $450"
                tabs={TOTAL_VALUE_OESTRA_TABS}
                eventData={eventData}
            />
            <Heading
                as="h3"
                size="2xs"
                sizes={{ md: "xs" }}
                weight="400"
                className="hidden md:inline-block leading-none"
            >
                per month
            </Heading>
            <span className="md:hidden basis-full h-0" />
            <Heading as="h3" size="2xs" sizes={{ md: "xs" }} weight="400" className="md:inline leading-none">for your</Heading>
            <Heading
                as="i"
                size="2xs"
                sizes={{ md: "xs" }}
                font="featureDisplay"
                weight="400"
                className="ml-1 leading-none"
            >
                first 6 months
            </Heading>
        </div>

        <OngoingPriceHeading size="2xs" sizes={{ md: "xs" }} />
    </>
);

const PricingSection = ({
    className = "lg:mb-12 max-md:mt-14",
    imageAlt = "Oestra® hormone cream being applied to hand",
    totalValueEventData = defaultTotalValueEventData,
}: Props) => {
    const isTotalValueEnabled = useTotalValue();

    return (
        <section
            className={cn(
                `
                relative overflow-hidden rounded-[24px]
                bg-[linear-gradient(180deg,_#19223D_2.38%,_#2A3966_39.12%,_#A9B9E5_161.17%)]
                lg:bg-[linear-gradient(177deg,_#1A274F_11.7%,_#A9B9E5_119.11%,_#E9EDF7_184.44%)]
            `,
                className
            )}
        >
            <ElementorContainer className="relative z-10 flex flex-col lg:min-h-[600px]" innerClassName="pt-12 lg:py-[72px]">
                <div className="w-full text-pureWhite max-md:text-center">
                    <Reveal>
                        <Heading as="h2" size="sm" sizes={{ md: "md" }} className="mb-4">
                            Simple, transparent <Heading as="i" weight="400" font="featureDisplay" className="text-[28px] md:text-[36px]">pricing</Heading>
                        </Heading>
                    </Reveal>

                    {isTotalValueEnabled ? (
                        <RedesignPricingHeadings
                            eventData={totalValueEventData ?? undefined}
                        />
                    ) : (
                        <LegacyPricingHeadings />
                    )}

                    <ul className="flex w-full lg:w-[55%] flex-wrap gap-2">
                        {pricingItems.map(({ id, icon, title }) => (
                            <li
                                key={id}
                                className="flex flex-col w-[45%] md:w-[48%] flex-grow items-center gap-1 rounded-2xl bg-trueBlack/10 p-4 text-center md:flex-row md:gap-5 md:py-3 md:px-6 md:text-left"
                            >
                                <div className="shrink-0">{icon}</div>
                                <Paragraph as="h4" size="lg" sizes={{ md: "xl" }} weight="500">
                                    {title}
                                </Paragraph>
                            </li>
                        ))}
                    </ul>

                    <DoseAdjustmentNote className="mt-4 md:mt-6 text-center md:text-left" />
                </div>
            </ElementorContainer>

            <div className="lg:absolute bottom-0 left-0 lg:left-[48%] 2xl:left-[54%] w-full lg:w-[720px] max-lg:mt-2">
                <Image
                    src="https://res.cloudinary.com/dirscuuhp/image/upload/v1785312774/design_system/oestra_weucgw.webp"
                    alt={imageAlt}
                    sizes="(max-width: 767px) 100vw, 60vw"
                    width={800}
                    height={777}
                    quality={95}
                    className="lg:aspect-[800/640] object-cover object-top"
                />
            </div>
        </section>
    );
};

export default PricingSection;
